import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Monitor, Moon, Sun, UserRound, type LucideIcon } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { useProfile } from '../profile/ProfileProvider';
import { useTheme, BETA_PREFS, type ThemePref } from '../design/theme';
import { Icon } from '../design/Icon';

const THEME_OPTIONS: { pref: ThemePref; label: string; icon: LucideIcon }[] = [
  { pref: 'dark', label: 'Dark', icon: Moon },
  { pref: 'light', label: 'Light', icon: Sun },
  { pref: 'system', label: 'System', icon: Monitor },
];

/** Avatar with initials fallback on surface-3 (also when the photo fails to load). */
export function Avatar({ size = 32 }: { size?: 24 | 32 }) {
  const { state } = useProfile();
  const [broken, setBroken] = useState(false);
  if (state.status !== 'ready') return null;
  const name = state.user.displayName || state.user.email;
  const cls = size === 24 ? 'w-6 h-6 text-2xs' : 'w-8 h-8 text-xs';
  return state.user.photoURL && !broken
    ? <img src={state.user.photoURL} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} className={`${cls} rounded-full object-cover shrink-0`} />
    : <span className={`${cls} rounded-full bg-surface-3 text-fg font-medium flex items-center justify-center shrink-0`}>{name.charAt(0).toUpperCase()}</span>;
}

/**
 * User menu (design system v1): Profile, Theme (System / Light / Dark)
 * and Sign out. Lives at the bottom of the sidebar (desktop) and in the top bar
 * (mobile). Esc and an outside click close it.
 */
export function UserMenu({ placement, collapsed = false }: { placement: 'sidebar' | 'topbar'; collapsed?: boolean }) {
  const { signOut } = useAuth();
  const { state } = useProfile();
  const { pref, setPref } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown); };
  }, [open]);

  if (state.status !== 'ready') return null;
  const name = state.user.displayName || state.user.email;

  const panelPos = placement === 'topbar'
    ? 'top-full right-0 mt-2'
    : collapsed ? 'left-full bottom-0 ml-2' : 'bottom-full left-0 right-0 mb-2';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={placement === 'topbar' || collapsed ? `Account menu for ${name}` : undefined}
        onClick={() => setOpen((o) => !o)}
        className={placement === 'topbar'
          ? 'flex items-center rounded-full'
          : `w-full flex items-center gap-2.5 rounded-md py-1.5 text-sm text-fg-muted hover:bg-surface-2 hover:text-fg transition-colors duration-fast ${collapsed ? 'justify-center px-0' : 'px-2.5'} ${open ? 'bg-surface-2 text-fg' : ''}`}
      >
        <Avatar size={placement === 'topbar' ? 24 : 32} />
        {placement === 'sidebar' && !collapsed && <span className="truncate">{name}</span>}
      </button>

      {open && (
        <div role="menu" className={`absolute ${panelPos} z-dropdown w-64 rounded-lg border border-line bg-surface text-fg shadow-md p-1.5`}>
          <div className="px-2.5 pt-1.5 pb-2 border-b border-line mb-1.5">
            <div className="text-sm font-medium truncate">{name}</div>
            <div className="text-xs text-fg-muted truncate">{state.user.email}</div>
          </div>
          <Link role="menuitem" to="/profile" onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 h-8 px-2.5 rounded-md text-sm hover:bg-surface-2">
            <Icon as={UserRound} className="text-fg-muted" />My profile
          </Link>
          <div className="px-2.5 pt-2.5 pb-1.5">
            <div className="text-2xs font-semibold uppercase text-fg-faint mb-1.5">Theme</div>
            <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-0.5 p-0.5 rounded-lg bg-surface-2">
              {THEME_OPTIONS.map((o) => (
                <button key={o.pref} type="button" role="radio" aria-checked={pref === o.pref} onClick={() => setPref(o.pref)}
                  className={`flex flex-col items-center gap-0.5 py-1.5 rounded-md text-xs transition-colors duration-fast ${
                    pref === o.pref ? 'bg-surface text-fg font-medium shadow-xs' : 'text-fg-muted hover:text-fg'}`}>
                  <Icon as={o.icon} />
                  <span>{o.label}{BETA_PREFS.has(o.pref) && <span className="ml-1 text-fg-faint font-normal">beta</span>}</span>
                </button>
              ))}
            </div>
            {BETA_PREFS.has(pref) && (
              <p className="mt-1.5 text-xs text-fg-muted">Beta: some pages are still built for Dark.</p>
            )}
          </div>
          <button role="menuitem" type="button" onClick={() => void signOut()}
            className="mt-1 w-full flex items-center gap-2.5 h-8 px-2.5 rounded-md text-sm hover:bg-surface-2">
            <Icon as={LogOut} className="text-fg-muted" />Sign out
          </button>
        </div>
      )}
    </div>
  );
}
